import { scrollToElement } from "@opten/scroll/scroll-to-element";
import { Component } from "@opten/compiler/metadata/component";

declare const Cookiebot: any;

interface Location {
	element: HTMLElement;
	position: google.maps.LatLng;
	marker: google.maps.Marker;
	url?: string;
	content: string;
	button: HTMLElement;
}

@Component({ className: "js-map" })
export class MapComponent {
	private instance: google.maps.Map;
	private infoWindow: google.maps.InfoWindow;
	private geoCoder: google.maps.Geocoder;
	private locations: Location[];

	private mapHolder: HTMLElement;

	constructor(private element: HTMLElement) {
		window.addEventListener(
			"CookiebotOnAccept",
			(e) => {
				this.cookiebotInit();
			},
			false
		);
		this.cookiebotInit();
	}

	private cookiebotInit() {
		if ("Cookiebot" in window && Cookiebot.consent.marketing) {
			this.geoCoder = new google.maps.Geocoder();
			this.infoWindow = new google.maps.InfoWindow();

			this.mapHolder = this.element.querySelector(".js-map-placeholder");
			Promise.all(
				Array.from(
					this.element.querySelectorAll(".js-map-location")
				).map((elem) => this.parseLocation(elem as HTMLElement))
			).then((locations) => {
				this.init(this.mapHolder, locations);
			});
		}
	}

	// tslint:disable-next-line: max-func-body-length
	private init(placeholder: Element, locations: Location[]) {
		this.instance = new google.maps.Map(placeholder, {
			center: { lat: 47.4133597, lng: 8.5342391 },
			zoom: 15,
			maxZoom: 25,
			disableDefaultUI: true,
			scrollwheel: false,
			styles: MapStyles,
		});

		locations.forEach((location) => {
			location.marker = this.addMarker(location);
			if (location.button) {
				location.button.addEventListener("click", () => {
					this.showLocation(location);
				});
			} else {
				location.element.addEventListener("click", () => {
					this.showLocation(location);
				});
			}
		});

		this.locations = locations;

		this.showAllLocations();
	}

	private parseLocation(div: HTMLElement) {
		return new Promise<Location>((resolve) => {
			const infobox = div.querySelector(".js-map-location-info");
			const location = {
				element: div,
				url: div.getAttribute("data-link"),
				content: infobox ? infobox.innerHTML.trim() : undefined,
				button: div.querySelector(".js-map-pan2location"),
			} as Location;

			if (div.getAttribute("data-location")) {
				const latlng = div.getAttribute("data-location").split(",");
				location.position = new google.maps.LatLng(
					+latlng[0],
					+latlng[1]
				);
				resolve(location);
			} else {
				const address = div.getAttribute("data-address");
				this.geoCoder.geocode({ address }, (results, status) => {
					if (status === google.maps.GeocoderStatus.OK) {
						location.position = results[0].geometry.location;
						resolve(location);
					} else {
						resolve(null);
					}
				});
			}
		});
	}

	private addMarker(location: Location): google.maps.Marker {
		const marker = new google.maps.Marker({
			position: location.position,
			map: this.instance,
			icon: " ",
			label: {
				fontFamily: "icomoon",
				fontSize: "30px",
				text: "\ue91c",
			},
		});

		marker.addListener("click", () => {
			this.showLocation(location);
		});

		return marker;
	}

	private showLocation(location: Location): void {
		if (location.url) {
			window.location.href = location.url;
		} else if (location.content) {
			scrollToElement(this.mapHolder, undefined, undefined, -50);
			this.infoWindow.setContent(location.content);
			this.infoWindow.open(this.instance, location.marker);
		}

		this.instance.setCenter(location.position);
		this.instance.setZoom(12);
	}

	private showAllLocations() {
		if (this.locations.length > 1) {
			const bounds = new google.maps.LatLngBounds();
			for (let i = 0; i < this.locations.length; i++) {
				bounds.extend(this.locations[i].position);
			}

			this.instance.fitBounds(bounds, 30);
		} else {
			this.instance.setCenter(this.locations[0].position);
		}
	}
}

export var MapStyles: google.maps.MapTypeStyle[] = [
	{
		elementType: "labels.icon",
		stylers: [
			{
				visibility: "off",
			},
		],
	},
	{
		featureType: "administrative.country",
		elementType: "geometry.stroke",
		stylers: [
			{
				color: "#AE0270",
			},
			{
				visibility: "on",
			},
		],
	},
	{
		featureType: "administrative.province",
		elementType: "geometry.stroke",
		stylers: [
			{
				color: "#AE0270",
			},
			{
				visibility: "on",
			},
			{
				weight: 2,
			},
		],
	},
	{
		featureType: "poi.park",
		elementType: "geometry",
		stylers: [
			{
				color: "#e5e5e5",
			},
		],
	},
	{
		featureType: "road.arterial",
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#757575",
			},
		],
	},
	{
		featureType: "road.highway",
		elementType: "labels.text.fill",
		stylers: [
			{
				color: "#616161",
			},
		],
	},
	{
		featureType: "water",
		elementType: "geometry",
		stylers: [
			{
				color: "#c9c9c9",
			},
		],
	},
];
